// client/src/components/common/UnifiedDatePicker.tsx
//
// One date, or one date and time (ST-00204) — the shared picker behind the single-value
// LexiStar fields: planned start, override "until", corridor expiry, split point, "who was
// driving at", fuel "takes effect", a report's day, the daily summary's day.
//
// One picker, two containers (PickerSurface), with Cancel / Apply: the page's quick times if it
// gives any (ST-00264), one month with a Today action, and the hour / minute grids with "Type a
// time" when there is a time (PickerTimeChooser). Days outside `minDate` / `maxDate` / `bounds`
// cannot be chosen. A field that may be empty has a clear "×" and a Clear action.
//
// Desktop (ST-00294): the field looks as the text box it replaced did — the page's `className`
// (height, border, width) on a button showing the value as `dateFormat` prints it, or the
// placeholder while empty. It opens a popup under the field (PickerPopover) with a text box on
// top for typing the date (and time) — 2026/09/25, 25/09/2026 06:30 … — opened with its text
// selected, Enter applies; then the calendar, and the time chooser beside it when there is a
// time. (`timeFormat`, `timeIntervals` and `autoComplete`, the old text box's settings, are
// accepted and no longer used: times go on the 5-minute grid or are typed to the minute.)
// Phone / touch (usePickerSheetMode): a full-width 44 px button opening the bottom sheet.
//
// Label it with <label htmlFor={id}>: the id lands on the button (desktop and phone), and the
// label names the popup.

import { useRef, useState, type ReactNode } from 'react';
import { format as formatWith } from 'date-fns';
import PickerSurface from './PickerSurface';
import PickerCalendar from './PickerCalendar';
import PickerTimeChooser from './PickerTimeChooser';
import PickerTimePresets from './PickerTimePresets';
import PickerTypedField, { type TypedCheck } from './PickerTypedField';
import { revealTime, usePickerSheetMode } from './pickerHooks';
import {
  atTime,
  dayKey,
  formatPickerDateTime,
  formatPickerDay,
  formatTypedDate,
  formatTypedDateTime,
  isDayDisabled,
  parseTypedDate,
  parseTypedDateTime,
  startOfDay,
  startOfMonth,
  timeOf,
  type PickerBounds,
  type TimePreset,
} from './pickerLogic';

export interface UnifiedDatePickerProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  /** Pick a time as well. */
  withTime?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  /** A window relative to today, on top of min / max. */
  bounds?: PickerBounds;
  placeholder?: string;
  /** Phone button text while empty, where `placeholder` is a typing mask ("yyyy/mm/dd"). */
  sheetPlaceholder?: string;
  /** The field may be emptied (× on the field, Clear in the popup). */
  clearable?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  /** Popup heading, e.g. "Planned start". */
  title?: string;
  /**
   * With a time: quick-time chips above the calendar (e.g. PLANNING_TIME_PRESETS: Now, +30 min,
   * +1 h, +2 h, Tomorrow 06:00). Chips outside the bounds / min / max are left out.
   */
  presets?: readonly TimePreset[];
  /** Desktop field text, in date-fns tokens (default yyyy/MM/dd, with a time yyyy/MM/dd HH:mm). */
  dateFormat?: string;
  /** Accepted for the fields that set it; no longer used. */
  timeFormat?: string;
  /** Accepted for the fields that set it; no longer used (5-minute grid + typed minutes). */
  timeIntervals?: number;
  /** Desktop field classes (the old text box's: height, border, width). */
  className?: string;
  /** Classes for the desktop field's wrapper (e.g. its width). */
  wrapperClassName?: string;
  /** Accepted for the fields that set it; no longer used (the field is a button). */
  autoComplete?: string;
}

/** The desktop field when the page gives no classes: the app's standard text box. */
const DEFAULT_FIELD_CLASS =
  'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const CalendarIcon = () => (
  <svg className="picker-field__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** A new day keeps the time already chosen; a first pick defaults to now's time, on the 5-min grid. */
const defaultTime = () => {
  const now = new Date();
  return { h: now.getHours(), m: Math.floor(now.getMinutes() / 5) * 5 };
};

export default function UnifiedDatePicker({
  value,
  onChange,
  withTime = false,
  minDate = null,
  maxDate = null,
  bounds = 'all',
  placeholder,
  sheetPlaceholder,
  clearable = false,
  disabled = false,
  id,
  'aria-label': ariaLabel,
  title,
  presets,
  dateFormat,
  className,
  wrapperClassName,
}: UnifiedDatePickerProps) {
  const sheetMode = usePickerSheetMode();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const activeDayRef = useRef<HTMLButtonElement | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const timeRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<Date | null>(null);
  const [typedInvalid, setTypedInvalid] = useState(false);
  const [fieldInvalid, setFieldInvalid] = useState(false);
  // The field's own <label htmlFor={id}> names the popup ("Until", "Split at", "Expires" …).
  const [labelText, setLabelText] = useState<string | null>(null);
  const [month, setMonth] = useState<Date>(() => startOfMonth(value ?? new Date()));

  const format = withTime ? formatPickerDateTime : formatPickerDay;
  const emptyText = sheetPlaceholder || placeholder;
  const limits = { bounds, min: minDate, max: maxDate };
  const pendingBlocked = !!pending && isDayDisabled(pending, limits);
  const blocked =
    pendingBlocked || (withTime && typedInvalid) || (!sheetMode && fieldInvalid);
  const surfaceOpen = open && !disabled;

  const openPicker = () => {
    if (disabled) return;
    setLabelText(triggerRef.current?.labels?.[0]?.textContent?.trim() || null);
    setPending(value);
    setFieldInvalid(false);
    setMonth(startOfMonth(value ?? new Date()));
    setOpen(true);
  };

  const apply = () => {
    if (blocked) return;
    onChange(pending);
    setOpen(false);
  };

  // ── Desktop text box ──────────────────────────────────────────────────────

  const printTyped = withTime ? formatTypedDateTime : formatTypedDate;
  const readTyped = (text: string): Date | null =>
    withTime
      ? parseTypedDateTime(text, pending ? timeOf(pending) : defaultTime())
      : parseTypedDate(text);

  const limitError = (d: Date): string => {
    if (minDate && dayKey(d) < dayKey(minDate)) return `Choose ${formatTypedDate(minDate)} or later.`;
    if (maxDate && dayKey(d) > dayKey(maxDate)) return `Choose ${formatTypedDate(maxDate)} or earlier.`;
    if (isDayDisabled(d, { bounds })) {
      return bounds === 'past' ? 'Cannot select future dates' : 'Cannot select past dates';
    }
    return '';
  };

  const checkTyped = (text: string): TypedCheck => {
    if (!text.trim()) return { error: '' };
    const d = readTyped(text);
    if (!d) {
      return {
        error: withTime ? 'Type it like 2026/09/25 07:00.' : 'Type it like 2026/09/25.',
        unreadable: true,
      };
    }
    return { error: limitError(d) };
  };

  const typeValue = (text: string): string | undefined => {
    if (!text.trim()) {
      // Emptied: no value where the field may be empty; otherwise nothing changes.
      if (!clearable) return undefined;
      setPending(null);
      return '';
    }
    const d = readTyped(text);
    if (!d || limitError(d)) return undefined;
    setPending(d);
    setMonth(startOfMonth(d));
    return printTyped(d);
  };

  // Desktop with a time: the calendar and the time chooser side by side.
  const sideBySide = (calendar: ReactNode, time: ReactNode) =>
    !sheetMode && withTime ? (
      <div className="picker-dt">
        {calendar}
        {time}
      </div>
    ) : (
      <>
        {calendar}
        {time}
      </>
    );

  const picker = (
    <PickerSurface
      variant={sheetMode ? 'sheet' : 'popover'}
      open={surfaceOpen}
      title={title ?? ariaLabel ?? labelText ?? (withTime ? 'Choose a date and time' : 'Choose a day')}
      onCancel={() => setOpen(false)}
      onApply={apply}
      applyDisabled={blocked}
      returnFocusRef={triggerRef}
      initialFocusRef={sheetMode ? activeDayRef : fieldRef}
      headerAction={
        clearable && value ? (
          <button
            type="button"
            className="picker-sheet__text-action"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            Clear
          </button>
        ) : undefined
      }
    >
      {sheetMode ? (
        <p className={`picker-hint${pending ? ' picker-hint--value' : ''}`} aria-live="polite">
          {pending ? format(pending) : emptyText || 'Nothing chosen yet'}
        </p>
      ) : (
        <div className="picker-typed-row">
          <PickerTypedField
            label={withTime ? 'Date and time' : 'Date'}
            value={pending ? printTyped(pending) : ''}
            placeholder={withTime ? 'YYYY/MM/DD HH:MM' : 'YYYY/MM/DD'}
            check={checkTyped}
            onType={typeValue}
            onEnter={apply}
            onInvalidChange={setFieldInvalid}
            inputRef={fieldRef}
            completeLength={withTime ? 16 : 10}
          />
        </div>
      )}
      {withTime && presets && presets.length > 0 && (
        <PickerTimePresets
          presets={presets}
          value={pending}
          limits={limits}
          onPick={(at) => {
            setPending(at);
            setMonth(startOfMonth(at));
          }}
        />
      )}
      {sideBySide(
        <PickerCalendar
          month={month}
          onMonthChange={setMonth}
          selected={pending}
          bounds={bounds}
          min={minDate}
          max={maxDate}
          onSelect={(day) => {
            setPending(withTime ? atTime(day, pending ? timeOf(pending) : defaultTime()) : startOfDay(day));
            if (withTime) revealTime(timeRef.current);
          }}
          activeDayRef={activeDayRef}
        />,
        withTime && (
          <PickerTimeChooser
            ref={timeRef}
            label="Time"
            value={pending ? timeOf(pending) : defaultTime()}
            onChange={(t) => setPending(atTime(pending ?? new Date(), t))}
            // The hint / text box above already says the whole date and time.
            announce={false}
            onTypedInvalidChange={setTypedInvalid}
            onEnter={sheetMode ? undefined : apply}
          />
        )
      )}
    </PickerSurface>
  );

  if (!sheetMode) {
    const pattern = dateFormat ?? (withTime ? 'yyyy/MM/dd HH:mm' : 'yyyy/MM/dd');
    let shown = '';
    if (value) {
      try {
        shown = formatWith(value, pattern);
      } catch {
        shown = printTyped(value);
      }
    }
    const canClear = clearable && !!value && !disabled;
    return (
      <div
        className={[
          'picker-input',
          canClear ? 'picker-input--clearable' : '',
          // No wrapper classes: a block, so a w-full field fills its column instead of shrinking
          // round its text.
          wrapperClassName ?? 'picker-input--block',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          ref={triggerRef}
          id={id}
          type="button"
          className={`picker-input__button ${className ?? DEFAULT_FIELD_CLASS}`}
          onClick={openPicker}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={surfaceOpen}
          {...(ariaLabel ? { 'aria-label': `${ariaLabel}: ${value ? shown : placeholder || 'not set'}` } : {})}
        >
          {value ? (
            <span className="picker-input__text">{shown}</span>
          ) : (
            // A non-breaking space keeps the field's height when there is no placeholder.
            <span className="picker-input__text picker-input__placeholder">{placeholder || ' '}</span>
          )}
        </button>
        {canClear && (
          <button type="button" className="picker-input__clear" onClick={() => onChange(null)} aria-label="Clear">
            <ClearIcon />
          </button>
        )}
        {picker}
      </div>
    );
  }

  return (
    <div className={`picker-field${disabled ? ' picker-field--disabled' : ''}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="picker-field__main"
        onClick={openPicker}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': `${ariaLabel}: ${value ? format(value) : emptyText || 'not set'}` } : {})}
      >
        {value ? (
          <span className="picker-field__text">{format(value)}</span>
        ) : (
          <span className="picker-field__text picker-field__placeholder">{emptyText || 'Choose…'}</span>
        )}
        <CalendarIcon />
      </button>
      {clearable && value && !disabled && (
        <button type="button" className="picker-field__clear" onClick={() => onChange(null)} aria-label="Clear">
          <ClearIcon />
        </button>
      )}
      {picker}
    </div>
  );
}
